import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { isReservedKey, parseEvent } from "../key-util.js";
import styles from "./KeyPicker.module.scss";

interface Props {
  currentKey?: string;
  onKeyPicked?: (key: string) => void;
  // When this token changes to a new truthy value the picker enters selection
  // mode automatically (used to chain left -> right rebinding).
  autoSelectToken?: any;
}

export interface KeyPickerHandle {
  onClick: () => void;
  // Enter selection mode synchronously (unlike the autoSelectToken effect, which
  // only fires after React commits). Used for left -> right chaining so a very
  // fast second keypress lands on the right picker instead of being dropped.
  select: () => void;
}

// A module-level set of keys currently held down, kept up to date on the capture
// phase so it's accurate before any picker's own handler runs. When a picker
// enters selection we snapshot this, and ignore those keys until they're released
// - that's what lets the right picker skip the left key you're still holding when
// you rebind both in one fast motion (press left, then right, without waiting).
const heldKeys = new Set<string>();
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (e) => { const k = parseEvent(e as any); if (k) heldKeys.add(k); }, true);
  document.addEventListener("keyup", (e) => { const k = parseEvent(e as any); if (k) heldKeys.delete(k); }, true);
  if (typeof window !== "undefined") window.addEventListener("blur", () => heldKeys.clear());
}

export default forwardRef<KeyPickerHandle, Props>(function KeyPicker(
  { currentKey, onKeyPicked, autoSelectToken },
  ref
) {
  const [selected, setSelectedState] = useState(false);
  // Mirror state in refs so the once-registered document listeners read live
  // values, not a stale closure.
  const selectedRef = useRef(false);
  // Keys that were already down when we entered selection - ignored until released
  // so the key that triggered the chain doesn't immediately get grabbed here.
  const ignoreRef = useRef<Set<string>>(new Set());
  const onKeyPickedRef = useRef(onKeyPicked);
  onKeyPickedRef.current = onKeyPicked;

  function enter() {
    selectedRef.current = true;
    ignoreRef.current = new Set(heldKeys);
    setSelectedState(true);
  }
  function exit() {
    selectedRef.current = false;
    setSelectedState(false);
  }
  function toggle() {
    if (selectedRef.current) exit();
    else enter();
  }

  useImperativeHandle(ref, () => ({ onClick: toggle, select: enter }), []);

  useEffect(() => {
    // Commit on keydown (not keyup): it's more responsive and, crucially, robust
    // to fast/overlapping presses - the moment a fresh key goes down while we're
    // selected, it's the binding. Keys held over from before selection are skipped
    // until released.
    function onKeyDown(event: KeyboardEvent) {
      if (!selectedRef.current) return;
      event.preventDefault();
      const key = parseEvent(event);
      if (!key || ignoreRef.current.has(key)) return;
      if (key === "ESC") {
        exit(); // cancel without changing the binding
      } else if (!isReservedKey(key)) {
        exit();
        onKeyPickedRef.current?.(key);
      }
    }
    function onKeyUp(event: KeyboardEvent) {
      const key = parseEvent(event);
      if (key) ignoreRef.current.delete(key); // released -> a re-press counts as fresh
    }
    function onMouseUp() {
      if (selectedRef.current) exit();
    }
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Auto-enter selection when the chain token flips to a new truthy value.
  const prevTokenRef = useRef(autoSelectToken);
  useEffect(() => {
    const prev = prevTokenRef.current;
    prevTokenRef.current = autoSelectToken;
    if (autoSelectToken && autoSelectToken !== prev && !selectedRef.current) enter();
  }, [autoSelectToken]);

  let className = styles.nmKeyChip;
  if (selected) {
    className += " " + styles.nmKeyChipSelected;
  } else if (!currentKey) {
    className += " " + styles.nmKeyChipMissing;
  }
  const label = selected ? "press a key" : currentKey || "?";

  return (
    <div className={className} onClick={toggle} style={onKeyPicked ? { cursor: "pointer" } : {}}>
      <div className="animation-size-expand">{label}</div>
    </div>
  );
});
